import { memo, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import usePlaybackWindow from 'hooks/transaction/playback/usePlaybackWindow';
import usePlaybackActions from 'hooks/transaction/playback/usePlaybackActions';
import { selectPlaybackIsVideoPlaying, selectPlaybackTransactionId } from 'store/playbackSlice/playbackSelectors';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import { getNowFormattedDate } from 'utils/getNowFormatedDate';
import { IMAGE_CATEGORIES } from '../panels/transaction/cameras/MediaSection/constants';

import PlaybackVideo from './PlaybackVideo';
import ResizeHandle from './ResizeHandle';
import MinimizeWindow from '../panels/transaction/VideoWallMode/CompactMode/CameraStreamWindow/MinimizeWindow';
import MaximizeWindow from '../panels/transaction/VideoWallMode/CompactMode/CameraStreamWindow/MaximizeWindow';
import CloseWindow from '../panels/transaction/VideoWallMode/CompactMode/CameraStreamWindow/CloseWindow';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import Replay30Icon from '@mui/icons-material/Replay30';
import Replay5Icon from '@mui/icons-material/Replay5';
import Forward30Icon from '@mui/icons-material/Forward30';
import Forward5Icon from '@mui/icons-material/Forward5';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PropTypes from 'prop-types';

import styles from './PlaybackWindow.module.css';

const PlaybackWindow = ({ isOpen, onClose, playbackUrl, isLoading, cameraName }) => {
    const popupRef = useRef(null);
    const closeBtnRef = useRef(null);
    const dispatch = useDispatch();

    const isPlaying = useSelector(selectPlaybackIsVideoPlaying);
    const transactionId = useSelector(selectPlaybackTransactionId);

    const { state, onMouseDown, onResizeMouseDown, handleMinimize, handleMaximize, handleClose } = usePlaybackWindow({
        isOpen,
        onClose,
    });

    const {
        handlePlayPause,
        handleSkipBack30,
        handleSkipForward30,
        handleSkipBack5,
        handleSkipForward5,
        handlePrevFrameMouseDown,
        handleNextFrameMouseDown,
        stopHoldFrame,
        handleTakeSnapshot,
    } = usePlaybackActions(popupRef);

    const { width, height, left, top } = state;

    const windowStyle = {
        width,
        height: height + 10,
        left,
        top,
    };

    const headerActionStyle = {
        color: '#fff',
        width: 24,
        height: 24,
    };

    const onSnapshotClick = useCallback(() => {
        const base64 = handleTakeSnapshot();
        const snapshot = base64.split(',').at(1);

        const payload = {
            transactionId: Number(transactionId),
            snapshot,
            id: Date.now(),
            imageCategory: IMAGE_CATEGORIES.PLAYBACK_SNAPSHOT,
            imageName: `Playback ${cameraName || 'Camera'} ${getNowFormattedDate()}`,
            cameraName: cameraName || null,
        };

        dispatch(formControlActions.saveCameraStreamSnapshot(payload));
    }, [handleTakeSnapshot, dispatch, transactionId, cameraName]);

    useEffect(() => {
        if (isOpen && closeBtnRef.current) {
            closeBtnRef.current.focus();
        }
    }, [isOpen]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const focused = document.activeElement;
            if (focused && focused.tagName === 'BUTTON') {
                focused.click();
            }
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            const focused = document.activeElement;
            if (focused) {
                focused.blur();
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div ref={popupRef} className={styles['playback-window--wrapper']} style={windowStyle} onKeyDown={handleKeyDown}>
            <div className={styles['playback-window--header']} onMouseDown={onMouseDown}>
                <div className={styles['playback-window-header--left']}>
                    <span className={styles['playback-window-header--badge']}>Playback</span>
                    <span className={styles['playback-window-header--camera-name']}>{cameraName || 'Camera'}</span>
                </div>

                <div className={styles['playback-window-header--controls-wrapper']}>
                    <div className={styles['playback-window-header--controls']}>
                        <Tooltip title="Go back 30 sec">
                            <IconButton size="small" onClick={handleSkipBack30} className={styles['playback-control-btn']}>
                                <Replay30Icon sx={headerActionStyle} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Go back 5 sec">
                            <IconButton size="small" onClick={handleSkipBack5} className={styles['playback-control-btn']}>
                                <Replay5Icon sx={headerActionStyle} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Previous frame (Press and hold to go frame by frame)">
                            <IconButton
                                size="small"
                                onMouseDown={handlePrevFrameMouseDown}
                                onMouseUp={stopHoldFrame}
                                onMouseLeave={stopHoldFrame}
                                className={styles['playback-control-btn']}
                            >
                                <ChevronLeftIcon sx={headerActionStyle} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
                            <IconButton size="small" onClick={handlePlayPause} className={styles['playback-control-btn--main']}>
                                {isPlaying ? <PauseIcon sx={headerActionStyle} /> : <PlayArrowIcon sx={headerActionStyle} />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Next frame (Press and hold to go frame by frame)">
                            <IconButton
                                size="small"
                                onMouseDown={handleNextFrameMouseDown}
                                onMouseUp={stopHoldFrame}
                                onMouseLeave={stopHoldFrame}
                                className={styles['playback-control-btn']}
                            >
                                <ChevronRightIcon sx={headerActionStyle} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Go forward 5 secs">
                            <IconButton size="small" onClick={handleSkipForward5} className={styles['playback-control-btn']}>
                                <Forward5Icon sx={headerActionStyle} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Go forward 30 secs">
                            <IconButton size="small" onClick={handleSkipForward30} className={styles['playback-control-btn']}>
                                <Forward30Icon sx={headerActionStyle} />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>

                <div className={styles['playback-window-header--snapshot']}>
                    <Tooltip title="Snapshot">
                        <IconButton size="small" onClick={onSnapshotClick} className={styles['playback-control-btn']}>
                            <CameraAltIcon sx={headerActionStyle} />
                        </IconButton>
                    </Tooltip>
                </div>

                <div className={styles['playback-window-header--actions']}>
                    <MinimizeWindow minimizeHandler={handleMinimize} tabIndex={0} />
                    <MaximizeWindow maximizeHandler={handleMaximize} tabIndex={0} />
                    <CloseWindow closeHandler={handleClose} tabIndex={0} ref={closeBtnRef} />
                </div>
            </div>

            <div className={styles['playback-window--content']}>
                {isLoading ? (
                    <div className={styles['playback-video--loading']}>
                        <CircularProgress size={40} sx={{ color: '#fff' }} />
                    </div>
                ) : (
                    <PlaybackVideo url={playbackUrl} />
                )}
            </div>

            <ResizeHandle side="right" onMouseDown={onResizeMouseDown('right')} />
            <ResizeHandle side="left" onMouseDown={onResizeMouseDown('left')} />
            <ResizeHandle side="bottom" onMouseDown={onResizeMouseDown('bottom')} />
        </div>
    );
};

PlaybackWindow.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    playbackUrl: PropTypes.string,
    isLoading: PropTypes.bool,
    cameraName: PropTypes.string,
};

export default memo(PlaybackWindow);
